import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { io } from "socket.io-client";
import { getSocketIoUrl, SOCKET_PATH } from "../config/apiConstants";
import { isAdminApiEnabled } from "../config/adminApiFeature";

const SocketContext = createContext(null);

/**
 * Single Socket.io client for the app. Disconnects when `active` is false.
 * @param {{ children: React.ReactNode, active: boolean }} props
 */
export function SocketProvider({ children, active }) {
  const [socket, setSocket] = useState(null);

  useEffect(() => {
    if (!active || !isAdminApiEnabled()) {
      setSocket((prev) => {
        if (prev) {
          prev.removeAllListeners();
          prev.disconnect();
        }
        return null;
      });
      return undefined;
    }

    const instance = io(getSocketIoUrl(), {
      path: SOCKET_PATH,
      transports: ["websocket", "polling"],
      withCredentials: true,
      autoConnect: true,
      reconnectionAttempts: 8,
      reconnectionDelay: 1200,
    });

    setSocket(instance);

    return () => {
      instance.removeAllListeners();
      instance.disconnect();
      setSocket(null);
    };
  }, [active]);

  const value = useMemo(() => ({ socket }), [socket]);

  return (
    <SocketContext.Provider value={value}>{children}</SocketContext.Provider>
  );
}

export function useSocketContext() {
  return useContext(SocketContext);
}

/** @returns {import('socket.io-client').Socket | null} */
export function useSocket() {
  const ctx = useContext(SocketContext);
  return ctx?.socket ?? null;
}
